import parseGoToDate from '../bundles/CalendarViewV2/components/parseGoToDate';

const TRAILING_TIME = /^(.*?)(?:\s*,?\s*(?:\bat\b|@)\s*|\s+)(\d{1,2})(?::(\d{2}))?\s*(?:([ap])\.?(?:m\.?)?)?$/i;

const FLATPICKR_DEFAULT_HOUR = 12;

const toHour24 = (hour, meridiem) => {
  if (!meridiem) return hour <= 23 ? hour : null;
  if (hour < 1 || hour > 12) return null;
  const isPm = meridiem.toLowerCase() === 'p';
  return (hour % 12) + (isPm ? 12 : 0);
};

const parseTypedEntry = (value, today) => {
  if (!value || !value.trim()) return null;

  const normalized = value.trim().replace(/\s+/g, ' ');
  const match = normalized.match(TRAILING_TIME);
  const hasTime = Boolean(match) && (match[3] !== undefined || match[4] !== undefined);

  const datePart = hasTime ? match[1] : normalized;
  const date = parseGoToDate(datePart, today);
  if (!date) return null;

  let hour = FLATPICKR_DEFAULT_HOUR;
  let minute = 0;
  if (hasTime) {
    hour = toHour24(parseInt(match[2], 10), match[4]);
    minute = match[3] === undefined ? 0 : parseInt(match[3], 10);
    if (hour === null || minute > 59) return null;
  }

  date.setHours(hour, minute, 0, 0);
  return { date, hasTime };
};

export const parseTypedDateTime = (value, today = new Date()) => {
  const entry = parseTypedEntry(value, today);
  return entry ? entry.date : null;
};

export const confirmDateTimePickerProps = ({
  typedEntry,
  dateFormat,
  minDate,
  minDateMessage = "That date and time is in the past.",
  unavailableMessage = "That date and time isn't available for this field.",
  options: extraOptions,
  value,
  defaultTime,
  onDates,
  onInvalidEntry,
}) => {
  const options = { enableTime: true, dateFormat };
  if (minDate !== undefined) options.minDate = minDate;
  Object.assign(options, extraOptions);

  const withDefaultTime = (dates, instance) => {
    if (!defaultTime || dates.length !== 1 || instance.defaultTimeApplied) return dates;
    const adjusted = defaultTime(dates[0]);
    const { minDate: earliest, maxDate: latest } = instance.config;
    if ((earliest && adjusted < earliest) || (latest && adjusted > latest)) return dates;
    instance.defaultTimeApplied = true;
    instance.setDate(adjusted, false);
    return [adjusted];
  };

  if (!typedEntry) {
    if (!defaultTime) return { options, onClose: onDates };
    return {
      options,
      onClose: onDates,
      onChange: (dates, dateText, instance) => { withDefaultTime(dates, instance); },
    };
  }

  let entryRejected = false;
  let pendingEntry = null;
  let lastTypedEntry = null;
  const rejectEntry = (message) => {
    entryRejected = true;
    onInvalidEntry?.(message);
  };
  const typedMeta = (entry) => ({ typed: true, hasTime: entry.hasTime });

  return {
    options: {
      ...options,
      allowInput: true,
      parseDate: (text) => {
        pendingEntry = null;
        const entry = parseTypedEntry(text, new Date());
        if (!entry) {
          rejectEntry("Couldn't read that date. Try something like 10/3/26 8pm.");
          return undefined;
        }
        if (minDate && entry.date < minDate) {
          rejectEntry(minDateMessage);
        }
        pendingEntry = entry;
        return entry.date;
      },
    },
    onClose: (dates, dateText, instance) => {
      const entry = lastTypedEntry;
      pendingEntry = null;
      if (entry && dates[0] && dates[0].getTime() === entry.date.getTime()) {
        onDates(dates, dateText, instance, typedMeta(entry));
        return;
      }
      onDates(dates, dateText, instance);
    },
    onChange: (dates, dateText, instance) => {
      const entry = pendingEntry;
      pendingEntry = null;
      if (!entryRejected && entry && dates.length === 0) {
        rejectEntry(unavailableMessage);
      }
      if (entryRejected) {
        entryRejected = false;
        instance.setDate(value || [], false);
        return;
      }
      lastTypedEntry = entry;
      if (entry) {
        onDates(dates, dateText, instance, typedMeta(entry));
        return;
      }
      onDates(withDefaultTime(dates, instance), dateText, instance);
    },
  };
};
